import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, map, catchError, of, tap, switchMap, combineLatest, throwError } from 'rxjs';
import { MembershipService } from './membership.service';
import { AzureDevOpsMembershipResponse, CarvanaAzureActiveDirectoryResponse } from '../models/membership-response';
import { AdoUserProfile } from '../models/ado-user-profile';
import { AdoService } from './ado.service';
import { AdoAccount } from '../models/ado-account';
import { AdoResponse } from '../models/ado-response';
import { AdoProject } from '../models/ado-project';
import { AuthService, TokenClaims } from './auth.service';
import { OwnershipService } from './ownership.service';
import { OwnershipEntry } from '../models/ownership-entry';
import { TeamOwnerMinorEntry } from '../models/team-owner-minor-entry';
import { UserSettingsService } from './user-settings-service';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { datadogRum } from '@datadog/browser-rum';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';
import { UserSettings } from '../models/user-settings';

/**
 * UserProfileService is the central authentication service for the application.
 * It handles user profile information and authentication state.
 * All components should use this service for authentication and user information.
 */
@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private userProfile: AdoUserProfile | null = null;
  private readonly profileInitialized = new BehaviorSubject<boolean>(false);
  private readonly isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private readonly userRoles$ = new BehaviorSubject<string[]>([]);
  private readonly userName$ = new BehaviorSubject<string>('');
  private readonly isImpersonatingUser$ = new BehaviorSubject<boolean>(false);

  // Public observables
  profileInitialized$ = this.profileInitialized.asObservable();

  // Merge auth status changes with our profile loading for a complete status
  private readonly authenticationStatusChanged$ = this.authService.getAuthStatusChanges().pipe(
    switchMap(isAuthenticated => this.handleAuthenticationChange(isAuthenticated))
  );

  constructor(
    private readonly membershipService: MembershipService,
    private readonly adoService: AdoService,
    private readonly authService: AuthService,
    private readonly ownershipService: OwnershipService,
    private readonly userSettingsService: UserSettingsService
  ) {
    // Initialize authentication state
    this.initializeAuthentication().subscribe();
  }

  /**
   * Initialize authentication process and start listening for auth changes
   * Components should subscribe to this to get notified of auth status changes
   * @returns Observable<boolean> - Observable that emits the authentication status
   */
  initializeAuthentication(): Observable<boolean> {
    return this.authenticationStatusChanged$;
  }

  /**
   * Handles authentication state changes
   * @param isAuthenticated - Whether the user is authenticated
   * @returns Observable<boolean> - Authentication status with profile loaded
   */
  private handleAuthenticationChange(isAuthenticated: boolean): Observable<boolean> {
    this.isAuthenticated$.next(isAuthenticated);

    if (!isAuthenticated) {
      this.profileInitialized.next(false);
      this.userProfile = null;
      this.userName$.next('');
      this.userRoles$.next([]);
      return of(false);
    }

    // Update user info from claims
    this.updateUserInfoFromClaims();

    return this.getCurrentProfile().pipe(
      map(profile => {
        this.profileInitialized.next(!!profile);

        // Update Datadog RUM with user info if profile is loaded
        if (profile) {
          datadogRum.setUser({
            id: profile.id,
            name: this.userName$.value,
            email: profile.emailAddress
          });
        }

        return !!profile;
      }),
      catchError(error => {
        console.error('Error fetching user profile:', error);
        this.profileInitialized.next(false);
        return of(false);
      })
    );
  }

  /**
   * Updates user information from token claims
   */
  private updateUserInfoFromClaims(): void {
    const claims = this.getActiveClaims();
    if (claims) {
      // Update user name
      if (claims.name) {
        this.userName$.next(claims.name as string);
      }

      // Update user roles
      if (claims.roles) {
        // Filter out Platform.Admin role if in impersonation mode
        if (this.isImpersonatingUser$.value) {
          const filteredRoles = (claims.roles as string[]).filter(role => role !== 'Platform.Admin');
          this.userRoles$.next(filteredRoles);
        } else {
          this.userRoles$.next(claims.roles);
        }
      }
    }
  }

  /**
   * Gets the combined state of authentication and profile initialization
   * @returns Observable<boolean> - True when both authenticated and profile loaded
   */
  getAuthenticationStatus(): Observable<boolean> {
    return combineLatest([
      this.isAuthenticated(),
      this.profileInitialized$
    ]).pipe(
      map(([isAuthenticated, profileInitialized]) =>
        isAuthenticated && profileInitialized
      )
    );
  }

  /**
   * Subscribes to authentication status changes and automatically updates
   * the provided component's isAuthenticated property
   *
   * @param component - The component to update
   * @param destroySubject - The component's destroy subject for cleanup
   */
  monitorAuthenticationStatus(component: { isAuthenticated: boolean }, destroySubject: Observable<void>): void {
    // Monitor interaction status changes
    this.getInteractionStatus().pipe(
      filter(status => status === InteractionStatus.None),
      takeUntil(destroySubject)
    ).subscribe(() => {
      // Update component's authentication state
      this.isAuthenticated().subscribe(isAuthenticated => {
        component.isAuthenticated = isAuthenticated;
      });
    });
  }

  /**
   * Initiates the login process
   */
  login(): void {
    this.authService.loginRedirect();
  }

  /**
   * Logs the user out
   */
  logout(): void {
    this.authService.logoutRedirect();
    this.userProfile = null;
    this.profileInitialized.next(false);
    this.isAuthenticated$.next(false);
    this.userName$.next('');
    this.userRoles$.next([]);
  }

  /**
   * Gets the current user profile as an observable
   * @returns Observable<AdoUserProfile | null>
   */
  getCurrentProfile(): Observable<AdoUserProfile | null> {
    return this.adoService.GetUserProfile().pipe(
      tap(profile => this.userProfile = profile)
    );
  }

  /**
   * Gets the current authentication state
   * @returns Observable<boolean>
   */
  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$.asObservable();
  }

  /**
   * Gets the current user roles
   * Takes into account impersonation mode
   * @returns Observable<string[]>
   */
  getUserRoles(): Observable<string[]> {
    return this.userRoles$.asObservable();
  }

  /**
   * Gets the current user name
   * @returns Observable<string>
   */
  getUserName(): Observable<string> {
    return this.userName$.asObservable();
  }

  /**
   * Checks if the user has a specific role
   * @param role - The role to check
   * @returns Observable<boolean>
   */
  hasRole(role: string): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(roles => roles.includes(role))
    );
  }

  /**
   * Checks if the user has any of the specified roles
   * @param roles - The roles to check
   * @returns Observable<boolean>
   */
  hasAnyRole(roles: string[]): Observable<boolean> {
    if (!roles || roles.length === 0) {
      return of(false);
    }

    return this.getUserRoles().pipe(
      map(userRoles => roles.some(role => userRoles.includes(role)))
    );
  }

  /**
   * Checks if the user is a member of the Platform team (has Platform.Admin role)
   * @returns Observable<boolean> - True if the user is a platform team member
   */
  isPlatformTeamMember(): Observable<boolean> {
    return this.hasRole('Platform.Admin');
  }

  /**
   * Gets the active account claims
   * @returns TokenClaims | null
   */
  getActiveClaims(): TokenClaims | null {
    return this.authService.getActiveClaims();
  }

  /**
   * Gets the user's email from active claims
   * @returns Observable<string> - The user's email or 'unknown user' if not available
   */
  getUserEmail(): Observable<string> {
    return of(this.getActiveClaims()?.email ?? 'unknown user');
  }

  /**
   * Gets the active account
   * @returns AccountInfo | null
   */
  getActiveAccount(): AccountInfo | null {
    return this.authService.getActiveAccount();
  }

  /**
   * Gets all accounts
   * @returns AccountInfo[]
   */
  getAllAccounts(): AccountInfo[] {
    return this.authService.getAllAccounts();
  }

  /**
   * Gets the InteractionStatus observable
   * @returns Observable<InteractionStatus>
   */
  getInteractionStatus(): Observable<InteractionStatus> {
    return this.authService.getInteractionStatus();
  }

  /**
   * Gets or initializes the Azure DevOps membership observable
   */
  getAzureDevOpsMembership(org: string): Observable<AzureDevOpsMembershipResponse> {
    return this.membershipService.getAzureDevOpsMembership(org);
  }

  /**
   * Gets or initializes the Carvana AD membership observable
   */
  private getCarvanaADMembership(org: string): Observable<CarvanaAzureActiveDirectoryResponse> {
    return this.membershipService.getCarvanaADMembership(org);
  }

  /**
   * Generic function to check if user is in any of the specified groups
   */
  private isUserInAnyGroups(groups: string[], userGroups: string[]): boolean {
    return groups.some(group => userGroups.includes(group));
  }

  /**
   * Gets both Azure DevOps groups and teams in a single call to avoid duplicate HTTP requests
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable with both groups and teams
   */
  getAzureDevOpsMembershipData(org: string): Observable<{
    groups: string[];
    teams: string[];
  }> {
    return this.getAzureDevOpsMembership(org).pipe(
      map(membership => ({
        groups: membership.groups.map(group => group.name),
        teams: membership.groups
          .filter(group => group.isTeam)
          .map(group => group.name)
      }))
    );
  }

  /**
   * Gets the user's Azure DevOps groups
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<string[]>
   */
  getAzureDevOpsGroups(org: string): Observable<string[]> {
    return this.getAzureDevOpsMembershipData(org).pipe(
      map(data => data.groups)
    );
  }

  /**
   * Gets only the team groups from Azure DevOps membership
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<string[]>
   */
  getAzureDevOpsTeams(org: string): Observable<string[]> {
    return this.getAzureDevOpsMembershipData(org).pipe(
      map(data => data.teams)
    );
  }

  /**
   * Gets only the team groups from Azure DevOps membership
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<string[]>
   */
  getAzureDevOpsTeamsForUser(org: string, userEmail: string): Observable<string[]> {
    return this.getAzureDevOpsMembership(org).pipe(
      map(data => data.groups.filter(
        group => group.members.userIdentities.map(
          i => i.toLowerCase()).includes(userEmail.toLowerCase())
        )
          .map(group => group.name)
        )
    );
  }

  /**
   * Gets the user's Carvana AD groups
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<string[]>
   */

  getCarvanaADGroups(org: string): Observable<string[]> {
    return this.getCarvanaADMembership(org).pipe(
      map(membership => membership.groups.map(group => group.name))

    );
  }

  /**
   * Gets the user's Carvana AD groups
   * @param org - The organization name (e.g., 'adesacentral')
   * @param userEmail - The user's email
   * @returns Observable<string[]>
   */

  getCarvanaADGroupsForUser(org: string, userEmail: string): Observable<string[]> {
    return this.getCarvanaADMembership(org).pipe(
      map(membership => membership.groups.filter(
          group => group.members.userIdentities.map(i => i.toLowerCase()).includes(userEmail.toLowerCase()))
          .map(group => group.name)
        )

    );
  }

  /**
   * Checks if a user belongs to any of the specified Azure DevOps groups
   * @param groupNames - Array of group names to check against
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<boolean>
   */
  isUserInAzureDevOpsGroups(groupNames: string[], org: string): Observable<boolean> {
    return this.getAzureDevOpsGroups(org).pipe(
      map(userGroups => this.isUserInAnyGroups(groupNames, userGroups))
    );
  }

  /**
   * Checks if a user belongs to any of the specified Azure DevOps teams
   * @param teamNames - Array of team names to check against
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<boolean>
   */
  isUserInAzureDevOpsTeams(teamNames: string[], org: string): Observable<boolean> {
    return this.getAzureDevOpsTeams(org).pipe(
      map(userTeams => this.isUserInAnyGroups(teamNames, userTeams))
    );
  }

  /**
   * Checks if a user belongs to any of the specified Carvana AD groups
   * @param groupNames - Array of group names to check against
   * @param org - The organization name (e.g., 'adesacentral')
   * @returns Observable<boolean>
   */
  isUserInCarvanaADGroups(groupNames: string[], org: string): Observable<boolean> {
    return this.getCarvanaADGroups(org).pipe(
      map(userGroups => this.isUserInAnyGroups(groupNames, userGroups))
    );
  }

  /**
   * Gets user accounts from Azure DevOps
   * @param publicAlias - The user's public alias
   * @returns Observable<AdoResponse<AdoAccount>>
   */
  getAccounts(publicAlias: string): Observable<AdoResponse<AdoAccount>> {
    return this.adoService.GetAccounts(publicAlias).pipe(
      catchError(error => {
        console.error('Error getting accounts:', error);
        return of({
          value: [],
          count: 0
        } as AdoResponse<AdoAccount>);
      })
    );
  }

  /**
   * Gets projects for a specific organization
   * @param org - The organization name
   * @returns Observable<any>
   */
  getProjects(org: string): Observable<AdoResponse<AdoProject>> {
    return this.adoService.GetProjects(org);
  }

  /**
   * Gets repository by ID
   * @param org - The organization name
   * @param project - The project name
   * @param repoName - The repository name
   * @returns Observable<any>
   */
  getRepositoryById(org: string, project: string, repoName: string): Observable<any> {
    return this.adoService.GetRepositoryById(org, project, repoName);
  }

  /**
   * Gets repository file content
   * @param org - The organization name
   * @param project - The project name
   * @param repoId - The repository ID
   * @param fileName - The file name
   * @returns Observable<any>
   */
  getRepositoryFileAsText(org: string, project: string, repoId: string, fileName: string): Observable<string> {
    return this.adoService.GetRepositoryFileAsText(org, project, repoId, fileName).pipe(
      catchError(error => {
        console.error('Error getting repository file:', error);
        throw error;
      })
    );
  }

  /**
   * Gets classic release link
   * @param org - The organization name
   * @param project - The project name
   * @param releaseId - The release ID
   * @returns string
   */
  getClassicReleaseLink(org: string, project: string, releaseId: string): string {
    return `https://dev.azure.com/${org}/${project}/_releaseProgress?_a=release-pipeline-progress&releaseId=${releaseId}`;
  }

  /**
   * Gets YAML release link
   * @param org - The organization name
   * @param project - The project name
   * @param releaseId - The release ID
   * @returns string
   */
  getYamlReleaseLink(org: string, project: string, releaseId: string): string {
    return `https://dev.azure.com/${org}/${project}/_build/results?buildId=${releaseId}`;
  }

  /**
   * Gets repository ownership master data
   * @param org - The organization name
   * @returns Observable<OwnershipEntry[]>
   */
  getRepoOwnershipMaster(org: string): Observable<OwnershipEntry[]> {
    return this.ownershipService.getRepoOwnershipMaster(org).pipe(
      catchError(error => {
        console.error('Error in getRepoOwnershipMaster:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Gets team owner minor data
   * @param org - The organization name
   * @returns Observable<TeamOwnerMinorEntry[]>
   */
  getTeamOwnerMinor(org: string): Observable<TeamOwnerMinorEntry[]> {
    return this.ownershipService.getTeamOwnerMinor(org);
  }

  getUserSettings(): Observable<UserSettings> {
    return this.userSettingsService.getUserSettings();
  }

  updateUserSettings(settings: UserSettings): Observable<void> {
    return this.userSettingsService.updateUserSettings(settings);
  }

  /**
   * Gets the combined membership data for a user, including inherited memberships
   * @param org - The organization name (e.g., 'adesacentral')
   * @param userEmail - The user's email address
   * @returns Observable with teams and groups the user is a member of (directly or through inheritance)
   */
  getUserFullMembership(org: string, userEmail: string): Observable<{
    teams: string[],
    groups: string[],
    adGroups: string[]
  }> {
    console.log(`[UserProfileService] Getting full membership for user ${userEmail} in org ${org}`);

    // Instead of using the private getAzureDevOpsMembership, we'll use the public getAzureDevOpsMembershipData
    // and then get the raw data from the membership service
    return this.membershipService.getAzureDevOpsMembership(org).pipe(
      tap(adoMembershipData => {
        console.log(`[UserProfileService] Received ADO membership data:`, {
          groups: adoMembershipData.groups.length,
          hasTeams: adoMembershipData.groups.some(g => g.isTeam),
          firstGroupExample: adoMembershipData.groups[0]?.name
        });
      }),
      switchMap(adoMembershipData => {
        // Get Carvana AD groups in parallel
        return this.getCarvanaADGroupsForUser(org, userEmail).pipe(
          tap(carvanaGroups => {
            console.log(`[UserProfileService] Received Carvana AD groups:`, {
              count: carvanaGroups.length,
              example: carvanaGroups[0]
            });
          }),
          map(carvanaGroups => {
            // Determine full membership using the membership data
            const allAdoGroups = adoMembershipData.groups;

            // Get the full membership
            const { teams, groups } = this.determineFullMembership(
              allAdoGroups,
              carvanaGroups,
              userEmail
            );

            console.log(`[UserProfileService] Determined full membership:`, {
              teamsCount: teams.length,
              groupsCount: groups.length,
              adGroupsCount: carvanaGroups.length,
              firstTeam: teams[0],
              firstGroup: groups[0]
            });

            return {
              teams,
              groups,
              adGroups: carvanaGroups
            };
          })
        );
      })
    );
  }

  /**
   * Determines the full membership (direct and inherited) for a user
   * @param allAdoGroups - All Azure DevOps groups
   * @param userCarvanaGroups - Carvana AD groups the user is a member of
   * @param userEmail - User's email for direct membership check
   * @returns Object containing teams and groups the user is a member of
   * @private
   */
  private determineFullMembership(
    allAdoGroups: any[],
    userCarvanaGroups: string[],
    userEmail: string
  ): { teams: string[], groups: string[] } {
    console.log(`[UserProfileService] Starting membership determination for ${userEmail}`);
    console.log(`[UserProfileService] Input: ${allAdoGroups.length} ADO groups, ${userCarvanaGroups.length} Carvana AD groups`);

    // Initialize sets to track membership (sets avoid duplicates)
    const memberGroups = new Set<string>();
    const memberTeams = new Set<string>();

    // Create map of group name to group for faster lookup
    const groupMap = new Map<string, any>();
    allAdoGroups.forEach(group => groupMap.set(group.name, group));

    // Check the structure of a sample group to validate our assumptions
    if (allAdoGroups.length > 0) {
      const sampleGroup = allAdoGroups[0];
      console.log(`[UserProfileService] Sample group structure:`, {
        name: sampleGroup.name,
        isTeam: sampleGroup.isTeam,
        hasMembers: !!sampleGroup.members,
        hasUserIdentities: !!sampleGroup.members?.userIdentities,
        hasGroupIdentities: !!sampleGroup.members?.groupIdentities,
        userIdentitiesCount: sampleGroup.members?.userIdentities?.length ?? 0,
        groupIdentitiesCount: sampleGroup.members?.groupIdentities?.length ?? 0
      });
    }

    // Process each group to check for direct or inherited membership
    let directMembershipCount = 0;
    let inheritedMembershipCount = 0;

    allAdoGroups.forEach(group => {
      if (this.isUserMemberOfGroup(group, userEmail, userCarvanaGroups, groupMap, new Set<string>())) {
        // Add to appropriate collection based on whether it's a team
        if (group.isTeam) {
          memberTeams.add(group.name);
        } else {
          memberGroups.add(group.name);
        }

        // Check if this was likely direct or inherited for debugging
        if (group.members?.userIdentities?.some((id: string) =>
          id.toLowerCase() === userEmail.toLowerCase()
        )) {
          directMembershipCount++;
        } else {
          inheritedMembershipCount++;
        }
      }
    });

    console.log(`[UserProfileService] Membership determination results:`, {
      directMemberships: directMembershipCount,
      inheritedMemberships: inheritedMembershipCount,
      totalTeams: memberTeams.size,
      totalGroups: memberGroups.size
    });

    // Convert sets to arrays
    return {
      teams: Array.from(memberTeams),
      groups: Array.from(memberGroups).filter(group => !memberTeams.has(group)) // Remove any duplicates
    };
  }

  /**
   * Recursively checks if a user is a member of a group (directly or inherited)
   * @param group - The group to check membership in
   * @param userEmail - User's email for direct membership check
   * @param userCarvanaGroups - Carvana AD groups the user is a member of
   * @param groupMap - Map of all groups for lookup
   * @param visitedGroups - Set of already checked groups to prevent circular references
   * @returns true if user is a member of the group
   * @private
   */
  private isUserMemberOfGroup(
    group: any,
    userEmail: string,
    userCarvanaGroups: string[],
    groupMap: Map<string, any>,
    visitedGroups: Set<string>
  ): boolean {
    // Prevent circular references
    if (visitedGroups.has(group.name)) {
      return false;
    }

    // Add current group to visited set
    visitedGroups.add(group.name);

    // Check direct user membership
    if (group.members?.userIdentities?.some((id: string) =>
      id.toLowerCase() === userEmail.toLowerCase()
    )) {
      return true;
    }

    // Check group membership inheritance
    for (const groupIdentity of group.members?.groupIdentities ?? []) {
      // Case 1: Inheritance from other Azure DevOps groups
      if (!groupIdentity.startsWith('[TEAM FOUNDATION]\\')) {
        const inheritedGroup = groupMap.get(groupIdentity);
        if (inheritedGroup && this.isUserMemberOfGroup(
          inheritedGroup, userEmail, userCarvanaGroups, groupMap, new Set(visitedGroups)
        )) {
          return true;
        }
      }
      // Case 2: Inheritance from Carvana AD groups
      else {
        // Extract the AD group name from the [TEAM FOUNDATION]\GroupName format
        const adGroupName = groupIdentity.replace('[TEAM FOUNDATION]\\', '');

        // Check if user is a member of this AD group
        if (userCarvanaGroups.some(g => g.toLowerCase() === adGroupName.toLowerCase())) {
          return true;
        }
      }
    }

    // User is not a member of this group
    return false;
  }

  /**
   * Toggles impersonation mode for admin users
   * When active, Platform.Admin role is temporarily removed from role checks
   * @param isImpersonating Whether impersonation mode is active
   */
  toggleImpersonation(isImpersonating: boolean): void {
    this.isImpersonatingUser$.next(isImpersonating);
    this.authService.toggleImpersonation(isImpersonating);

    // Update roles to reflect impersonation state
    this.updateUserInfoFromClaims();
  }

  /**
   * Checks if impersonation mode is active
   * @returns Observable<boolean>
   */
  isImpersonatingUser(): Observable<boolean> {
    return this.isImpersonatingUser$.asObservable();
  }

  /**
   * Checks if the user is an administrator (has Platform.Admin role)
   * This ignores the impersonation mode to allow admins to exit impersonation
   * @returns Observable<boolean> - True if the user is actually an admin
   */
  isActuallyPlatformAdmin(): Observable<boolean> {
    // Get claims directly from auth service
    const claims = this.getActiveClaims();
    const isAdmin = claims?.roles ? (claims.roles as string[]).includes('Platform.Admin') : false;
    return of(isAdmin);
  }

}
